/**
 * Age utility functions for handling text-based age input
 * Supports formats like "21 years", "6 months", "2 weeks", "3 days"
 */

export type AgeUnit = 'years' | 'months' | 'weeks' | 'days';

export interface ParsedAge {
  value: number;
  unit: AgeUnit;
  original: string;
}

/**
 * Parse age string into structured format
 * @param ageString - Age string like "21 years", "6 months", etc.
 * @returns Parsed age object or null if invalid
 */
export function parseAge(ageString: string): ParsedAge | null {
  if (!ageString || typeof ageString !== 'string') {
    return null;
  }

  const trimmed = ageString.trim();
  
  // Match pattern: number + unit (with optional 's' at end)
  const match = trimmed.match(/^(\d+)\s*(years?|months?|weeks?|days?)$/i);
  
  if (!match) {
    return null;
  }

  const value = parseInt(match[1], 10);
  const unit = match[2].toLowerCase().replace(/s$/, '') as AgeUnit;

  return {
    value,
    unit,
    original: trimmed
  };
}

/**
 * Validate age string format
 * @param ageString - Age string to validate
 * @returns Validation result with error message if invalid
 */
export function validateAge(ageString: string): { isValid: boolean; message: string } {
  if (!ageString || typeof ageString !== 'string') {
    return { isValid: false, message: 'Age is required' };
  }

  const trimmed = ageString.trim();
  
  if (!trimmed) {
    return { isValid: false, message: 'Age is required' };
  }

  const parsed = parseAge(trimmed);
  
  if (!parsed) {
    return { 
      isValid: false, 
      message: 'Please use format: "23 years", "6 months", "2 weeks", etc.' 
    };
  }

  // Reasonable range checks
  if (parsed.unit === 'years' && (parsed.value < 0 || parsed.value > 120)) {
    return { isValid: false, message: 'Age must be between 0-120 years' };
  }
  
  if (parsed.unit === 'months' && (parsed.value < 0 || parsed.value > 1440)) {
    return { isValid: false, message: 'Age must be between 0-1440 months' };
  }
  
  if (parsed.unit === 'weeks' && (parsed.value < 0 || parsed.value > 6240)) {
    return { isValid: false, message: 'Age must be between 0-6240 weeks' };
  }
  
  if (parsed.unit === 'days' && (parsed.value < 0 || parsed.value > 43800)) {
    return { isValid: false, message: 'Age must be between 0-43800 days' };
  }

  return { isValid: true, message: '' };
}

/**
 * Auto-append "years" to numeric input
 * @param input - User input string
 * @returns Formatted age string
 */
export function autoAppendYears(input: string): string {
  const trimmed = input.trim();
  
  // If input is only numbers, append "years"
  if (/^\d+$/.test(trimmed)) {
    return `${trimmed} years`;
  }
  
  return trimmed;
}

/**
 * Convert age to months for sorting/comparison
 * @param ageString - Age string
 * @returns Age in months (approximate)
 */
export function ageToMonths(ageString: string): number {
  const parsed = parseAge(ageString);
  
  if (!parsed) {
    return 0;
  }

  switch (parsed.unit) {
    case 'years':
      return parsed.value * 12;
    case 'months':
      return parsed.value;
    case 'weeks':
      return Math.round(parsed.value / 4.33);
    case 'days':
      return Math.round(parsed.value / 30.44);
    default:
      return 0;
  }
}

/**
 * Convert age to years for analytics/grouping
 * @param ageString - Age string
 * @returns Age in years (approximate)
 */
export function ageToYears(ageString: string): number {
  if (!ageString || typeof ageString !== 'string') {
    return 0;
  }

  // Handle plain numbers (e.g., "25" -> 25 years)
  const plainNumber = parseInt(ageString.trim(), 10);
  if (!isNaN(plainNumber) && plainNumber > 0 && plainNumber < 150) {
    return plainNumber;
  }

  // Handle formatted age strings (e.g., "25 years", "6 months")
  const months = ageToMonths(ageString);
  return Math.floor(months / 12);
}

/**
 * Format age for display with proper capitalization
 * @param ageString - Age string
 * @returns Formatted age string
 */
export function formatAgeForDisplay(ageString: string): string {
  const parsed = parseAge(ageString);
  
  if (!parsed) {
    return ageString; // Return original if can't parse
  }

  const unit = parsed.unit.charAt(0).toUpperCase() + parsed.unit.slice(1);
  return `${parsed.value} ${unit}`;
}

/**
 * Get age suggestions for incomplete input
 * @param input - Partial age input
 * @returns Array of suggested age strings
 */
export function getAgeSuggestions(input: string): string[] {
  const trimmed = input.trim();
  
  // If input is only numbers, suggest common units
  if (/^\d+$/.test(trimmed)) {
    const value = parseInt(trimmed, 10);
    
    // Suggest appropriate units based on value
    if (value <= 2) {
      return [`${value} years`, `${value * 12} months`];
    } else if (value <= 24) {
      return [`${value} months`, `${Math.round(value / 12)} years`];
    } else {
      return [`${value} years`];
    }
  }
  
  return [];
}

/**
 * Check if age string represents a pediatric patient
 * @param ageString - Age string
 * @returns True if pediatric (under 18 years)
 */
export function isPediatric(ageString: string): boolean {
  const years = ageToYears(ageString);
  return years < 18;
}

/**
 * Get appropriate age display format based on age
 * @param ageString - Age string
 * @returns Suggested display format
 */
export function getAgeDisplayFormat(ageString: string): string {
  const parsed = parseAge(ageString);
  
  if (!parsed) {
    return ageString;
  }

  // For very young children, show in months/weeks
  if (parsed.unit === 'years' && parsed.value < 2) {
    const months = parsed.value * 12;
    return `${months} months`;
  }
  
  // For young children, show both years and months
  if (parsed.unit === 'years' && parsed.value < 5) {
    const months = parsed.value * 12;
    const remainingMonths = months % 12;
    if (remainingMonths > 0) {
      return `${parsed.value} years ${remainingMonths} months`;
    }
  }
  
  // Default: show as entered
  return formatAgeForDisplay(ageString);
}

/**
 * Normalize age string to ensure it has proper format
 * @param ageString - Age string to normalize
 * @returns Normalized age string
 */
export function normalizeAgeString(ageString: string): string {
  if (!ageString || typeof ageString !== 'string') {
    return '';
  }
  
  const trimmed = ageString.trim();
  
  // If it's just a number, append "years"
  if (/^\d+$/.test(trimmed)) {
    return `${trimmed} years`;
  }
  
  // If it already has a unit, return as is
  if (/^\d+\s*(years?|months?|weeks?|days?)$/i.test(trimmed)) {
    return trimmed;
  }
  
  // If it's a number with some text, try to clean it up
  const match = trimmed.match(/^(\d+)\s*(.*)$/);
  if (match) {
    const number = match[1];
    const text = match[2].toLowerCase().trim();
    
    // Map common variations to standard units
    if (text.includes('year') || text.includes('yr')) {
      return `${number} years`;
    } else if (text.includes('month') || text.includes('mo')) {
      return `${number} months`;
    } else if (text.includes('week') || text.includes('wk')) {
      return `${number} weeks`;
    } else if (text.includes('day')) {
      return `${number} days`;
    }
  }
  
  return trimmed;
}
